import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";

const API_URL = `${getAPIbaseURL()}/work-packages`;

export const fetchWorkPackagesByProject = createAsyncThunk(
  "workPackages/fetchByProject",
  async (projectId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || i18n.t("slices.workPackages.errors.fetch")
      );
    }
  }
);

export const createWorkPackage = createAsyncThunk(
  "workPackages/create",
  async ({ projectId, data }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_URL}/project/${projectId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.workPackages.notifications.created"));
      return res.data;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.workPackages.errors.create");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const updateWorkPackage = createAsyncThunk(
  "workPackages/update",
  async ({ workPackageId, data }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API_URL}/${workPackageId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.workPackages.notifications.updated"));
      return res.data;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.workPackages.errors.update");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const deleteWorkPackage = createAsyncThunk(
  "workPackages/delete",
  async (workPackageId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${workPackageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.workPackages.notifications.deleted"));
      return workPackageId;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.workPackages.errors.delete");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

const workPackageSlice = createSlice({
  name: "workPackages",
  initialState: { items: [], isLoading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkPackagesByProject.fulfilled, (state, action) => {
        state.items = action.payload;
        state.isLoading = false;
      })
      .addCase(createWorkPackage.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.isLoading = false;
      })
      .addCase(updateWorkPackage.fulfilled, (state, action) => {
        state.items = state.items.map((wp) => (wp.id === action.payload.id ? action.payload : wp));
        state.isLoading = false;
      })
      .addCase(deleteWorkPackage.fulfilled, (state, action) => {
        state.items = state.items.filter((wp) => wp.id !== action.payload);
        state.isLoading = false;
      })
      .addMatcher(
        (action) => action.type.startsWith("workPackages/") && action.type.endsWith("/pending"),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("workPackages/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      );
  },
});

export default workPackageSlice.reducer;